import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Package, Calendar, Users, FileText, Clock, Banknote } from "lucide-react";
import { formatInTimeZone } from "date-fns-tz";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { useProductionMachineDetail } from "../hooks/useProductionMachineDetail";
import { t } from "@/shared/lib/th-translations";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Alert, AlertDescription } from "../components/ui/alert";
import ZScoreLegend from "../components/production/ZScoreLegend";
import { zScoreColor, hasEnoughBaseline } from "@/frontend/lib/production-stats";

type WindowDays = 1 | 14 | 30 | 60 | 90;
type DetailMetric = "good_units" | "shots" | "value";

const detailMetricOptions: { key: string; value: DetailMetric }[] = [
  { key: "production.machines.detail.metric.goodUnits", value: "good_units" },
  { key: "production.machines.detail.metric.shots", value: "shots" },
  { key: "production.machines.detail.metric.value", value: "value" },
];

const windowOptions: { key: string; value: WindowDays }[] = [
  { key: "production.machines.detail.window.today", value: 1 },
  { key: "production.machines.detail.window.last14Days", value: 14 },
  { key: "production.machines.detail.window.last30Days", value: 30 },
  { key: "production.machines.detail.window.last60Days", value: 60 },
  { key: "production.machines.detail.window.last90Days", value: 90 },
];

const THB_FORMATTER = new Intl.NumberFormat("th-TH", {
  style: "currency",
  currency: "THB",
  maximumFractionDigits: 0,
});

function formatTHB(amount: number): string {
  return THB_FORMATTER.format(amount);
}

const PIE_COLORS = [
  "#14b8a6",
  "#6366f1",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#10b981",
  "#f97316",
  "#06b6d4",
];

function formatDurationMs(ms: number): string {
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hours > 0) {
    return t("production.machines.detail.format.hourMinute")
      .replace("{h}", String(hours))
      .replace("{m}", String(mins));
  }
  return t("production.machines.detail.format.minute").replace("{m}", String(mins));
}

function formatTimestamp(ms: number): string {
  return formatInTimeZone(new Date(ms), "Asia/Bangkok", "dd MMM HH:mm");
}

export default function ProductionMachineDetail() {
  const { machine: rawMachine } = useParams<{ machine: string }>();
  const machineName = rawMachine ? decodeURIComponent(rawMachine) : "";
  const [windowDays, setWindowDays] = useState<WindowDays>(30);
  const [metric, setMetric] = useState<DetailMetric>("shots");

  const { data, isLoading, error } = useProductionMachineDetail(
    machineName || undefined,
    windowDays,
  );

  const responseData = data?.data;
  const totals = responseData?.totals;
  const baseline = responseData?.baseline;
  const baselineShots = responseData?.baselineShots;
  const dailyThroughput = responseData?.dailyThroughput ?? [];
  const operatorMix = responseData?.operatorMix ?? [];
  const operatorPeerStats = responseData?.operatorPeerStats;
  const topJobs = responseData?.topJobs ?? [];
  const idleGaps = responseData?.idleGaps ?? [];

  const longestIdleGap =
    idleGaps.length > 0 ? Math.max(...idleGaps.map((g) => g.durationMs)) : null;

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <Link
          to="/production/machines"
          className="text-sm text-blue-600 hover:underline flex items-center gap-1"
        >
          ← {t("production.machines.detail.backToList")}
        </Link>
        <h1 className="text-3xl font-bold">
          {t("production.machines.detail.title").replace("{machine}", machineName)}
        </h1>
      </div>

      {/* Controls row */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            {t("production.machines.detail.window.label")}:
          </span>
          <div className="flex gap-1">
            {windowOptions.map((opt) => (
              <Button
                key={opt.value}
                variant={windowDays === opt.value ? "default" : "outline"}
                size="sm"
                onClick={() => setWindowDays(opt.value)}
              >
                {t(opt.key)}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            {t("production.machines.detail.metric.label")}:
          </span>
          <div className="flex gap-1">
            {detailMetricOptions.map((opt) => (
              <Button
                key={opt.value}
                variant={metric === opt.value ? "default" : "outline"}
                size="sm"
                onClick={() => setMetric(opt.value)}
              >
                {t(opt.key)}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{t("production.machines.detail.error")}</AlertDescription>
        </Alert>
      )}

      {/* Stats row — 6 cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {metric === "shots"
                ? t("production.machines.detail.stats.totalShots")
                : metric === "value"
                  ? t("production.machines.detail.stats.totalValue")
                  : t("production.machines.detail.stats.totalGoodUnits")}
            </CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : metric === "value" ? (
                formatTHB(totals?.value ?? 0)
              ) : (
                (metric === "shots"
                  ? (totals?.shots ?? 0)
                  : (totals?.goodUnits ?? 0)
                ).toLocaleString()
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.machines.detail.stats.totalValue")}
            </CardTitle>
            <Banknote className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                formatTHB(totals?.value ?? 0)
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.machines.detail.stats.activeDays")}
            </CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? <Skeleton className="h-8 w-16" /> : (totals?.activeDays ?? 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.machines.detail.stats.uniqueOperators")}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? <Skeleton className="h-8 w-16" /> : (totals?.uniqueOperators ?? 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.machines.detail.stats.uniqueTasks")}
            </CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? <Skeleton className="h-8 w-16" /> : (totals?.uniqueTasks ?? 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.machines.detail.stats.longestIdleGap")}
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : longestIdleGap !== null ? (
                formatDurationMs(longestIdleGap)
              ) : (
                "—"
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Color hint */}
      <ZScoreLegend namespace="production.machines.detail.legend" />

      {/* Throughput trend */}
      <Card>
        <CardHeader>
          <CardTitle>{t("production.machines.detail.sections.throughputTrend")}</CardTitle>
          <p className="text-sm text-muted-foreground">
            {t("production.machines.detail.sections.throughputTrendSubtitle")}
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : dailyThroughput.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              {t("production.machines.detail.empty.machine")}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart
                data={dailyThroughput}
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="day"
                  tickFormatter={(v: string) => v.slice(5)}
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 12 }}
                  stroke="#14b8a6"
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 12 }}
                  stroke="#8b5cf6"
                />
                <Tooltip
                  formatter={(value, name) => [
                    Number(value).toLocaleString(),
                    String(name),
                  ]}
                  labelFormatter={(label) => String(label)}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {(() => {
                  const bl = metric === "shots" ? baselineShots : baseline;
                  const yAxisId = metric === "shots" ? "right" : "left";
                  return bl && bl.count >= 3 ? (
                    <ReferenceLine
                      yAxisId={yAxisId}
                      y={bl.mean}
                      stroke="#f59e0b"
                      strokeDasharray="4 4"
                      label={{
                        value: `avg ${bl.mean.toFixed(0)}`,
                        position: "insideTopRight",
                        fontSize: 11,
                      }}
                    />
                  ) : null;
                })()}
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="goodUnits"
                  stroke="#14b8a6"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  name={t("production.machines.detail.stats.totalGoodUnits")}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="shots"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  strokeDasharray="4 3"
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  name={t("production.machines.detail.stats.totalShots")}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Operator mix */}
      <Card>
        <CardHeader>
          <CardTitle>{t("production.machines.detail.sections.operatorMix")}</CardTitle>
          <p className="text-sm text-muted-foreground">
            {t("production.machines.detail.sections.operatorMixSubtitle")}
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : operatorMix.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              {t("production.machines.detail.empty.operators")}
            </div>
          ) : (
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="w-full md:w-72 flex-shrink-0">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                    <Pie
                      data={operatorMix}
                      dataKey={metric === "shots" ? "shots" : metric === "value" ? "value" : "goodUnits"}
                      nameKey="employeeName"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={40}
                    >
                      {operatorMix.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [
                        metric === "value"
                          ? formatTHB(Number(value))
                          : Number(value).toLocaleString(),
                        metric === "shots"
                          ? t("production.machines.detail.operatorTable.shots")
                          : metric === "value"
                            ? t("production.machines.detail.operatorTable.value")
                            : t("production.machines.detail.operatorTable.goodUnits"),
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="w-full overflow-x-auto">
                {operatorPeerStats?.soleOperator && (
                  <Alert className="mb-3">
                    <AlertDescription className="text-xs">
                      {t("production.machines.detail.operatorTable.soleOperatorBanner")}
                    </AlertDescription>
                  </Alert>
                )}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="px-3 py-2 text-left font-medium">
                        {t("production.machines.detail.operatorTable.name")}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {metric === "shots"
                          ? t("production.machines.detail.operatorTable.shots")
                          : metric === "value"
                            ? t("production.machines.detail.operatorTable.value")
                            : t("production.machines.detail.operatorTable.goodUnits")}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t("production.machines.detail.operatorTable.value")}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t("production.machines.detail.operatorTable.events")}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t("production.machines.detail.operatorTable.daysActive")}
                      </th>
                      <th className="px-3 py-2 text-center font-medium">
                        {t("production.machines.detail.operatorTable.peerRank")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {operatorMix.map((op, i) => {
                      const z = metric === "shots" ? op.zScoreShots : op.zScore;
                      const peerN = operatorPeerStats?.goodUnits.count ?? operatorMix.length;
                      const enough =
                        !operatorPeerStats?.soleOperator && hasEnoughBaseline(peerN);
                      const rankClass = enough
                        ? zScoreColor(z)
                        : "bg-gray-100 text-gray-600";
                      const rankText = operatorPeerStats?.soleOperator
                        ? t("production.machines.detail.operatorTable.soleOperator")
                        : !hasEnoughBaseline(peerN)
                          ? t(
                              "production.machines.detail.operatorTable.insufficientBaseline",
                            )
                          : z.toFixed(2);
                      const primaryValue =
                        metric === "shots"
                          ? op.shots
                          : metric === "value"
                            ? op.value
                            : op.goodUnits;
                      const primaryDisplay =
                        metric === "value"
                          ? formatTHB(primaryValue)
                          : primaryValue.toLocaleString();
                      return (
                        <tr
                          key={op.employeeId}
                          className="border-b hover:bg-gray-50/50"
                        >
                          <td className="px-3 py-2">
                            <span className="flex items-center gap-2">
                              <span
                                className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                                style={{
                                  backgroundColor: PIE_COLORS[i % PIE_COLORS.length],
                                }}
                              />
                              <Link
                                to={`/production/people/${encodeURIComponent(op.employeeId)}`}
                                className="text-blue-600 hover:underline"
                              >
                                {op.employeeName}
                              </Link>
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {primaryDisplay}
                          </td>
                          <td className="px-3 py-2 text-right text-muted-foreground">
                            {formatTHB(op.value)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {op.events.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-right">{op.daysActive}</td>
                          <td className="px-3 py-2 text-center">
                            <span
                              className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${rankClass}`}
                              title={
                                enough
                                  ? `${t("production.machines.detail.operatorTable.zScoreTooltip")}: ${z.toFixed(2)}, n=${peerN}`
                                  : t(
                                      "production.machines.detail.operatorTable.noPeerComparison",
                                    )
                              }
                            >
                              {rankText}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top jobs */}
      <Card>
        <CardHeader>
          <CardTitle>{t("production.machines.detail.sections.topJobs")}</CardTitle>
          <p className="text-sm text-muted-foreground">
            {t("production.machines.detail.sections.topJobsSubtitle")}
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : topJobs.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              {t("production.machines.detail.empty.jobs")}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="px-3 py-2 text-left font-medium">
                      {t("production.machines.detail.jobsTable.taskNumber")}
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      {t("production.machines.detail.jobsTable.customer")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
                      {t("production.machines.detail.jobsTable.paperWidth")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
                      {t("production.machines.detail.jobsTable.paperLength")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
                      {t("production.machines.detail.jobsTable.targetQuantity")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {metric === "shots"
                        ? t("production.machines.detail.jobsTable.shots")
                        : metric === "value"
                          ? t("production.machines.detail.jobsTable.value")
                          : t("production.machines.detail.jobsTable.goodUnits")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t("production.machines.detail.jobsTable.value")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t("production.machines.detail.jobsTable.events")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t("production.machines.detail.jobsTable.duration")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {topJobs.map((job) => {
                    const primaryValue =
                      metric === "shots"
                        ? job.shots
                        : metric === "value"
                          ? job.value
                          : job.goodUnits;
                    const primaryDisplay =
                      metric === "value"
                        ? formatTHB(primaryValue)
                        : primaryValue.toLocaleString();
                    return (
                      <tr key={job.taskNumber} className="border-b hover:bg-gray-50/50">
                        <td className="px-3 py-2 font-mono text-xs">{job.taskNumber}</td>
                        <td
                          className="px-3 py-2 max-w-[160px] truncate"
                          title={job.customerName}
                        >
                          {job.customerName}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-muted-foreground">
                          {job.paperWidth > 0 ? job.paperWidth : "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-muted-foreground">
                          {job.paperLength > 0 ? job.paperLength : "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-muted-foreground">
                          {job.targetQuantity > 0
                            ? job.targetQuantity.toLocaleString()
                            : "—"}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {primaryDisplay}
                        </td>
                        <td className="px-3 py-2 text-right text-muted-foreground">
                          {formatTHB(job.value)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {job.events.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {formatDurationMs(job.lastSeenMs - job.firstSeenMs)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Idle gaps */}
      <Card>
        <CardHeader>
          <CardTitle>{t("production.machines.detail.sections.idleGaps")}</CardTitle>
          <p className="text-sm text-muted-foreground">
            {t("production.machines.detail.sections.idleGapsSubtitle")}
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : idleGaps.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              {t("production.machines.detail.empty.idleGaps")}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="px-3 py-2 text-left font-medium">
                      {t("production.machines.detail.idleGapsTable.start")}
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      {t("production.machines.detail.idleGapsTable.end")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t("production.machines.detail.idleGapsTable.duration")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {idleGaps.map((gap, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50/50">
                      <td className="px-3 py-2 whitespace-nowrap">
                        {formatTimestamp(gap.startMs)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {formatTimestamp(gap.endMs)}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {formatDurationMs(gap.durationMs)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
